import { useCallback, useEffect, useRef, useState } from "react";

import { Button, Icon_Close24 } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { useParams } from "react-router-dom";

import commentIcon from "../../assets/images/comment-icon.svg";
import { type CommentID, useComments } from "../../hooks/useComments";
import usePrevious from "../../hooks/usePrevious";
import { useTracker } from "../../hooks/useTracker";
import { useWebExport } from "../../hooks/useWebExport";
import { useCommentsStore } from "../../stores/commentStore";
import { Comment } from "../Comment/Comment";
import { ToolButton } from "../ToolButton/ToolButton";

import style from "./CommentCarousel.module.css";

const commentGap = 8;
//300 + padding + gap
const commentWidth = 300 + 2 * 12 + commentGap;

export const CommentCarousel = () => {
  const tracker = useTracker();
  const { viewLinkId = "" } = useParams();
  const { data: commentsData, isSuccess: isCommentsSuccess } = useComments();
  const { data: webExportData, isSuccess: isWebExportSuccess } = useWebExport();
  const {
    openCommentId,
    setOpenCommentId,
    isNewCommentOpen,
    setIsNewCommentOpen,
    newCommentPosition,
  } = useCommentsStore();

  const handleCommentClick = (commentId: CommentID) => {
    if (openCommentId === commentId) {
      setIsCardOpen(true);
    } else {
      setOpenCommentId(commentId);
    }
  };

  const isLoaded = isCommentsSuccess && isWebExportSuccess;

  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const scrollHandler = useCallback(
    (e: React.UIEvent<HTMLElement>) => {
      if (openCommentId) {
        const scrollOffset =
          e.currentTarget.scrollLeft -
          (commentWidth - document.body.clientWidth / 2 - commentWidth / 2) -
          commentWidth +
          commentGap / 2;
        const cardIndex = Math.round(scrollOffset / commentWidth);
        // "Nearly divisible" calculation to account for subpixel scrolling browser differences
        const atSnappingPoint = Math.abs(commentWidth - (scrollOffset % commentWidth)) < 1;

        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = setTimeout(() => {
          if (atSnappingPoint) {
            setOpenCommentId(commentsData?.comments[cardIndex].commentId);
            if (commentsData?.comments[cardIndex].commentId) {
              tracker.trackCommentSelected({
                commentId: commentsData?.comments[cardIndex].commentId,
                webExportId: webExportData?.webExport.webExportId,
                source: "sidebar",
                viewLinkId,
              });
            }
          }
        }, 50);
      }
    },
    [
      openCommentId,
      setOpenCommentId,
      commentsData?.comments,
      tracker,
      webExportData?.webExport.webExportId,
      viewLinkId,
    ]
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollToCommentIndex = useCallback((commentIndex: number, behavior: ScrollBehavior) => {
    const scrollOffset = document.body.clientWidth / 2 - commentWidth / 2;
    scrollRef.current?.scrollTo({
      left: (commentIndex + 1) * commentWidth + scrollOffset,
      behavior,
    });
  }, []);

  const previousOpenCommentId = usePrevious(openCommentId);
  useEffect(() => {
    const commentIndex =
      commentsData?.comments.findIndex(({ commentId }) => commentId === openCommentId) ?? -1;
    if (commentIndex != -1) {
      scrollToCommentIndex(commentIndex, previousOpenCommentId ? "smooth" : "auto");
    }
  }, [previousOpenCommentId, openCommentId, commentsData?.comments, scrollToCommentIndex]);

  const [isCardOpen, setIsCardOpen] = useState(false);
  const openComment = isCardOpen
    ? commentsData?.comments?.find(({ commentId }) => commentId === openCommentId)
    : undefined;

  const [isCarouselOpen, setIsCarouselOpen] = useState(false);
  useEffect(() => {
    if (openCommentId) {
      setIsCarouselOpen(true);
    } else {
      setIsCarouselOpen(false);
    }
  }, [openCommentId]);

  return (
    <>
      <div
        className={classNames(style.container, isCarouselOpen && style.isOpen)}
        onScroll={scrollHandler}
        ref={scrollRef}
      >
        <div>
          <div style={{ width: "324px", height: "100px" }}></div>
        </div>
        {isLoaded && !commentsData?.comments?.length && (
          <div className={style.cardContainer}>
            <div className={style.card}>
              <div className={style.emptyCard}>No comments yet.</div>
            </div>
          </div>
        )}
        {commentsData?.comments?.map((comment) => {
          return (
            <div key={comment.commentId} className={style.cardContainer}>
              <div
                onClick={() => handleCommentClick(comment.commentId)}
                className={classNames(style.card)}
              >
                <div style={{ width: "300px" }}>
                  <Comment
                    isTruncated
                    initialMode="view"
                    title={commentsData?.users[comment.createdByUserId] ?? "Unknown user"}
                    date={comment.createdAt}
                  >
                    {comment.message}
                  </Comment>
                </div>
              </div>
            </div>
          );
        })}
        <div>
          <div style={{ width: "324px", height: "100px" }}></div>
        </div>
      </div>
      <ToolButton
        title="Comments"
        alt="Comments"
        className={style.toolButton}
        icon={commentIcon}
        onClick={() => {
          setIsCarouselOpen((prev) => !prev);
          tracker.trackCommentingSidebarVisibilityChanged({
            viewLinkId,
            visible: Boolean(!openCommentId),
          });
        }}
        isActive={isCarouselOpen}
      />

      <div
        className={classNames(style.openCard, (openComment || isNewCommentOpen) && style.isOpen)}
      >
        {isNewCommentOpen && (
          <Comment
            initialMode="new"
            title="New comment"
            position={newCommentPosition}
            dispose={({ newComment }) => {
              setIsNewCommentOpen(false);
              setOpenCommentId(newComment?.commentId);
            }}
            actions={
              <Button variant="borderless" onClick={() => setIsNewCommentOpen(false)}>
                <Icon_Close24 />
              </Button>
            }
          ></Comment>
        )}

        {openComment && (
          <Comment
            isTruncated={false}
            initialMode="view"
            title={commentsData?.users[openComment.createdByUserId] ?? "Unknown user"}
            date={openComment.createdAt}
            actions={
              <>
                <Comment.ContextMenu comment={openComment} />
                <Button variant="borderless" onClick={() => setIsCardOpen(false)}>
                  <Icon_Close24 />
                </Button>
              </>
            }
          >
            {openComment.message}
          </Comment>
        )}
      </div>
    </>
  );
};
